import { useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from './checkbox';
import TextLink from './TextLink';
import { translate } from '../../i18n/translate';
import { consentBox, consentText, useConsentLocale } from '../../utils/leadConsent';
import './ConsentCheckbox.css';

/**
 * ConsentCheckbox — one of the two contact-consent boxes on every lead form
 * (#1440), both unchecked by default:
 *  - `box="request"` — REQUIRED: the dealer may contact the visitor about this
 *    request, including through its AI assistant. The form can't be sent (or
 *    a walk-through moved on) until it's ticked: the browser's own validation
 *    stops the submit, with our text in the consent's language;
 *  - `box="marketing"` — optional: offers on similar cars.
 *
 * The component is controlled and only renders; the form turns the boxes into
 * the lead payload with `buildLeadConsents({ request, marketing }, locale)`.
 *
 * The privacy-policy link sits OUTSIDE the `<label>` so clicking it opens the
 * policy instead of toggling the box. It is hidden when the dealer has not
 * configured a privacy URL — we never link a dealer's visitor to our own
 * policy.
 */
export const ConsentCheckbox = ({
  id,
  box,
  checked = false,
  onCheckedChange,
  dealerName = null,
  privacyUrl = null,
  locale,
  disabled = false,
  className = '',
}) => {
  const autoId = useId().replace(/:/g, '');
  const inputId = id || `consent-${autoId}`;
  const resolvedLocale = useConsentLocale(locale);
  const text = consentText(resolvedLocale, dealerName, box);
  const privacyLabel = translate(resolvedLocale, 'leadConsent.privacyPolicy') ?? 'Privacy policy';
  const { required } = consentBox(box);
  const requiredText =
    translate(resolvedLocale, 'leadConsent.required') ?? 'Please tick this box to continue.';

  // The browser's message for an unticked required box would come in the
  // browser's language, not the consent's: set ours. (Shadow DOM — look the
  // input up from our own root, not the document.)
  const rootRef = useRef(null);
  useEffect(() => {
    const input = rootRef.current?.querySelector('input[type="checkbox"]');
    if (input) input.setCustomValidity(required && !checked ? requiredText : '');
  }, [required, checked, requiredText]);

  return (
    <div ref={rootRef} className={`consent-checkbox ${className}`.trim()} lang={resolvedLocale}>
      <Checkbox
        id={inputId}
        name={box === 'marketing' ? 'marketingConsent' : 'requestConsent'}
        className="consent-checkbox__input"
        checked={checked}
        disabled={disabled}
        required={required}
        onCheckedChange={onCheckedChange}
      />
      <p className="consent-checkbox__body">
        <label className="consent-checkbox__text" htmlFor={inputId}>
          {text}
        </label>
        {/* Same mark as the form's required fields; the box itself is
            `required` for assistive tech. */}
        {required && (
          <span className="consent-checkbox__required" aria-hidden="true">
            {' *'}
          </span>
        )}
        {privacyUrl && (
          <>
            {' '}
            <TextLink className="consent-checkbox__link" href={privacyUrl} external>
              {privacyLabel}
            </TextLink>
          </>
        )}
      </p>
    </div>
  );
};

ConsentCheckbox.propTypes = {
  id: PropTypes.string,
  /** Which box: `request` (required) or `marketing` (optional). */
  box: PropTypes.oneOf(['request', 'marketing']).isRequired,
  checked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  /** Dealer display name for `{dealer}`; blank → localized "the dealer". */
  dealerName: PropTypes.string,
  /** Dealer's privacy policy; the link is hidden when absent. */
  privacyUrl: PropTypes.string,
  /** Force a locale; default = provider language → explicit choice → en. */
  locale: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default ConsentCheckbox;
